import React, { useCallback } from 'react'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { FileUploadModal } from 'ui/components/FileUpload'
import { useGlobalModal } from 'ui/components/Modal/useGlobalModal'
import { Tag } from 'ui/components/Tag'

export const AttachmentDefaultValue: React.FC<{
    value: FieldDto['default_value']
    onChange: (value: FieldDto['default_value']) => void
}> = ({ value, onChange }) => {
    const { show } = useGlobalModal(FileUploadModal)

    const onUploadClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            show({
                zIndex: 9999,
                onUploadSuccess: (files) => {
                    const newAttachments = files.map((file) => ({
                        filename: file.name,
                        mimetype: file.type,
                        size: file.size,
                        url: file.url,
                    }))

                    onChange({
                        type: 'constant',
                        value: [...(value?.value ?? []), ...newAttachments],
                    })
                },
            })
        },
        [show, onChange, value?.value]
    )

    const onRemoveAttachment = useCallback(
        (url: string) => {
            onChange({
                type: 'constant',
                value: value?.value.filter((attachment: any) => {
                    if (typeof attachment === 'string') {
                        return attachment !== url
                    }

                    return attachment.url !== url
                }),
            })
        },
        [onChange, value?.value]
    )

    return (
        <Box flex column flexWrap="wrap" gap="xs" shrink alignSelf="stretch">
            <Box flex flexWrap="wrap" gap="xs" pt={value?.value?.length ? 'l' : undefined} mb="s">
                {value?.value?.map((attachment: any) => (
                    <Box key={attachment.url} minWidth={0} maxWidth="full">
                        <Tag
                            size="xs"
                            startIcon={{ name: 'Paperclip' }}
                            type="outline"
                            shade="light"
                            color="Neutral"
                            noShrink
                            tabIndex={-1}
                            style={{
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                            }}
                            showRemoveButton
                            onRemove={() => onRemoveAttachment(attachment.url)}
                        >
                            {attachment.filename}
                        </Tag>
                    </Box>
                ))}
            </Box>
            <Box>
                <Button
                    variant="secondary"
                    startIcon={{ name: 'Plus' }}
                    size="s"
                    onClick={onUploadClick}
                >
                    Upload file
                </Button>
            </Box>
        </Box>
    )
}
